import constant from '../api/constant';

/**
 * ENTER VICE admin API wrapper (SCRUM-219), same shape as theaApi.ts.
 * admin-service proxies `/admin/api/v1/entervice/*` to vice-service and answers with the house
 * envelope `{ statusCode, message, data }`. Errors arrive as `{ statusCode, message, type, reasons? }`;
 * `reasons` carries the activation-lock details of a 409 PRESET_ACTIVATION_BLOCKED.
 */
const PREFIX = '/api/v1/entervice';

export class EnterViceApiError extends Error {
  statusCode?: number;

  type?: string;

  reasons?: string[];

  constructor(message: string) {
    super(message);
    this.name = 'EnterViceApiError';
    // WHY: the ES5 build loses the subclass prototype on Error unless it is restored by hand.
    Object.setPrototypeOf(this, EnterViceApiError.prototype);
  }
}

const toApiError = (error: any): EnterViceApiError => {
  const payload = error?.response?.data;
  const apiError = new EnterViceApiError(payload?.message || payload?.error || error?.message || 'Request failed');
  apiError.statusCode = payload?.statusCode ?? error?.response?.status;
  apiError.type = payload?.type;
  apiError.reasons = Array.isArray(payload?.reasons) ? payload.reasons : undefined;
  return apiError;
};

type Method = 'get' | 'post' | 'put' | 'delete';

interface CallOptions {
  params?: Record<string, unknown>;
  timeout?: number;
}

async function call<T>(method: Method, path: string, body?: unknown, options: CallOptions = {}): Promise<T> {
  try {
    const res = await constant.axios.request({
      method,
      url: `${PREFIX}${path}`,
      data: body,
      params: options.params,
      ...(options.timeout ? { timeout: options.timeout } : {}),
    });
    return res.data?.data as T;
  } catch (error) {
    throw toApiError(error);
  }
}

export const enterviceGet = <T>(path: string, params?: Record<string, unknown>) => call<T>('get', path, undefined, { params });
export const entervicePost = <T>(path: string, body: unknown = {}) => call<T>('post', path, body);
export const entervicePut = <T>(path: string, body: unknown) => call<T>('put', path, body);
export const enterviceDelete = <T>(path: string) => call<T>('delete', path);
/** Multipart upload: axios drops the JSON content-type for FormData so the browser adds the boundary itself. */
export const entervicePostForm = <T>(path: string, form: FormData) => call<T>('post', path, form, { timeout: 60000 });
